import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import { getToken } from "../../services/Token";
import {
  useCreateKudosMutation,
  useGetEngageAudiencesQuery,
  useGetEngageRecipientsQuery,
  useGetRecognizeValuesQuery,
} from "../../api/engageApi";
import AudienceDialog from "./AudienceDialog";
import RecognizeValuesDialog from "./RecognizeValuesDialog";
import {
  MESSAGE_MAX_LENGTH,
  VALUE_CHIP_CLASS,
  findAudience,
  initials,
  normalizeKudos,
} from "./constants";

const GiveKudos = ({ onBack, onPosted }) => {
  const [recipientId, setRecipientId] = useState("");
  const [audienceId, setAudienceId] = useState("");
  const [values, setValues] = useState([]);
  const [message, setMessage] = useState("");
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [valuesOpen, setValuesOpen] = useState(false);

  const { data: users, isLoading: usersLoading } =
    useGetEngageRecipientsQuery();
  const { data: audiences } = useGetEngageAudiencesQuery();
  const { data: valueList } = useGetRecognizeValuesQuery();
  const [createKudos, { isLoading: posting }] = useCreateKudosMutation();

  // /api/engage/recipients returns employeeId as the human code ("ICS-10"),
  // and that code is what createKudos takes as recipientId. So the self check
  // uses getToken("Id") (decoded.employeeId, the code) - NOT
  // getToken("employeeId"), which is decoded.sub, the mongo id.
  const myCode = getToken("Id");

  const recipients = useMemo(() => {
    const rows = Array.isArray(users) ? users : [];
    return rows
      .filter((user) => user.employeeId && user.employeeId !== myCode)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [users, myCode]);

  const recipient = recipients.find((user) => user.employeeId === recipientId);
  const audience = findAudience(audiences, audienceId);

  // Look the label up from the fetched list rather than storing it alongside
  // the key, so a reworded description never goes stale in local state.
  const valueLabel = (key) => {
    const match = (valueList || []).find((item) => item.value === key);
    return match ? match.value.toLowerCase() : key.toLowerCase();
  };

  const trimmed = message.trim();
  const canSubmit = Boolean(recipientId && audienceId && trimmed) && !posting;

  const handleGive = async () => {
    if (!canSubmit) return;
    try {
      const created = await createKudos({
        recipientId,
        message: trimmed,
        recognizeValues: values,
        audienceId,
      }).unwrap();

      toast.success("Kudos posted successfully");
      if (onPosted) onPosted(normalizeKudos(created));
      onBack();
    } catch (error) {
      // The envelope carries the reason - "You cannot give kudos to yourself",
      // "Recipient not found" - so surface it rather than a generic failure.
      toast.error(error?.data?.message || "Could not post the kudos");
    }
  };

  return (
    <div className="p-6">
      <div className="mx-auto max-w-3xl overflow-hidden rounded-lg border border-gray-200 bg-white">
        {/* action bar */}
        <div className="flex items-center gap-3 border-b border-gray-200 px-5 py-3">
          <button
            type="button"
            onClick={onBack}
            className="rounded-md px-2 py-1 text-sm font-medium text-gray-500 hover:bg-gray-100"
          >
            &lsaquo; Back
          </button>
          <h2 className="flex-1 text-lg font-semibold text-gray-800">
            Give Kudos
          </h2>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleGive}
            className="flex items-center gap-2 rounded-md bg-blue-600 px-6 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {posting && <CircularProgress size={14} color="inherit" />}
            Give
          </button>
        </div>

        {/* selected recipient */}
        {recipient && (
          <div className="flex items-center gap-3 border-b border-gray-200 bg-blue-secondery-2 px-5 py-4">
            <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full border-2 border-green-500 bg-white text-sm font-semibold text-gray-700">
              {initials(recipient.name)}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold uppercase text-gray-800">
                {recipient.name}
              </p>
              {recipient.role && (
                <p className="text-xs text-gray-500">{recipient.role}</p>
              )}
              {audience && (
                <p className="text-xs text-gray-500">
                  {audience.sectionLabel}: {audience.name}
                </p>
              )}
            </div>
          </div>
        )}

        {/* recipient */}
        <div className="border-b border-gray-200 px-5 py-4">
          <label
            htmlFor="kudos-recipient"
            className="mb-1 block text-sm font-semibold text-gray-700"
          >
            Recipient <span className="text-red-500">*</span>
          </label>
          <p className="mb-2 text-xs text-gray-400">
            You are not in this list - kudos cannot be given to yourself.
          </p>
          {usersLoading ? (
            <CircularProgress size={18} />
          ) : (
            <select
              id="kudos-recipient"
              value={recipientId}
              onChange={(event) => setRecipientId(event.target.value)}
              className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Select a colleague...</option>
              {recipients.map((user) => (
                <option key={user.employeeId} value={user.employeeId}>
                  {user.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* audience */}
        <div className="border-b border-gray-200 px-5 py-4">
          <p className="mb-1 block text-sm font-semibold text-gray-700">
            Tag a group <span className="text-red-500">*</span>
          </p>
          <p className="mb-2 text-xs text-gray-400">
            Everyone can see this kudos. The tag just helps people filter the
            feed.
          </p>
          <button
            type="button"
            onClick={() => setAudienceOpen(true)}
            className="flex w-full max-w-md items-center gap-2 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-left text-sm text-gray-700 hover:border-blue-500"
          >
            <span className="flex-1">
              {audience
                ? `${audience.sectionLabel}: ${audience.name}`
                : "Select a group"}
            </span>
            <span className="text-gray-400">&rsaquo;</span>
          </button>
        </div>

        {/* recognize values */}
        <div className="border-b border-gray-200 px-5 py-4">
          <p className="mb-1 block text-sm font-semibold text-gray-700">
            Recognize Values
          </p>
          <p className="mb-3 text-xs text-gray-400">
            Appreciate the good in others by acknowledging their positive
            qualities and virtues.
          </p>
          <div className="flex items-start gap-3">
            <div className="flex flex-1 flex-wrap gap-2">
              {values.length === 0 && (
                <span className="text-xs text-gray-400">None selected</span>
              )}
              {values.map((key) => (
                <span key={key} className={`${VALUE_CHIP_CLASS} capitalize`}>
                  {valueLabel(key)}
                </span>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setValuesOpen(true)}
              aria-label="Choose values"
              className="flex h-8 w-8 flex-none items-center justify-center rounded-md border border-gray-200 bg-blue-secondery-1 text-lg font-semibold text-blue-700 hover:bg-blue-600 hover:text-white"
            >
              +
            </button>
          </div>
        </div>

        {/* message */}
        <div className="px-5 py-4">
          <label
            htmlFor="kudos-message"
            className="mb-2 block text-sm font-semibold text-gray-700"
          >
            Your Message <span className="text-red-500">*</span>
          </label>
          <textarea
            id="kudos-message"
            value={message}
            maxLength={MESSAGE_MAX_LENGTH}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Write a message of appreciation..."
            rows={4}
            className="w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-right text-xs text-gray-400">
            {message.length}/{MESSAGE_MAX_LENGTH}
          </p>
        </div>
      </div>

      <AudienceDialog
        open={audienceOpen}
        selectedId={audienceId}
        onClose={() => setAudienceOpen(false)}
        onConfirm={(id) => {
          setAudienceId(id);
          setAudienceOpen(false);
        }}
      />

      <RecognizeValuesDialog
        open={valuesOpen}
        selected={values}
        onClose={() => setValuesOpen(false)}
        onSelect={(keys) => {
          setValues(keys);
          setValuesOpen(false);
        }}
      />
    </div>
  );
};

export default GiveKudos;
